<template>
    <div class="layer-tree-item" :data-layer-id="getLayerId(value)">
        <component
            :is="itemComponent"
            v-if="!(isPageEps && shouldHideWrapper)"
            :item="value"
            :collapsed="isCollapsed"
            @toggle-collapse="toggleCollapse"
        />
        <div v-else style="padding: 1px 0" />
        <template v-if="!isCollapsed || forceShowIfOnlyHasOnePageEps">
            <draggable
                v-bind="dragOptions"
                :list="itemChildren"
                :group="{
                    name: 'layers-tree',
                    pull: true
                }"
                :move="onMove"
                @end="handleDragEnd"
                @input="emitModelUpdate"
                @change="onChangeHandler"
            >
                <template v-if="hasChildren && itemChildren">
                    <layers-tree-item
                        v-for="(child, index) in itemChildren"
                        :key="getItemKey(child)"
                        v-model="itemChildren[index]"
                    />
                </template>
                <template v-else>
                    <div class="no-children">This is empty</div>
                </template>
            </draggable>
        </template>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import draggable from 'vuedraggable';
import store from '@/store';

import type { ListElements } from '@/typings/customilyElements/layer';
import LayersTreeItem from './LayersTreeItem.vue';
import LayersTreeItemEps from '@/components/Layers/tree/item/eps/LayersTreeItemEps.vue';
import LayersTreeItemGroup from '@/components/Layers/tree/item/group/LayersTreeItemGroup.vue';
import LayersTreeItemElement from '@/components/Layers/tree/item/element/LayersTreeItemElement.vue';
import { useLayersDraggable, type MoveEvent } from '@/components/Layers/composables/layerDraggable';
import { useLayerTreeItem } from '@/components/Layers/composables/useLayerTreeItem';

const props = defineProps<{
    value: ListElements;
}>();

const emit = defineEmits<{
    (event: 'update:modelValue', value: ListElements[]): void;
    (event: 'tree-item-moved'): void;
}>();

const {
    pageEpsElements,
    dragOptions,
    handleMove,
    getItemKey,
    handleChange
} = useLayersDraggable();

const { isCollapsed, onCollapsedChanged } = useLayerTreeItem({
    item: props.value
});

const itemChildren = computed(() => props.value.children);
const hasChildren = computed(() => itemChildren.value && itemChildren.value.length > 0);

const isPageEps = computed(() => props.value.type === 'PageEps');
const shouldHideWrapper = computed(() => pageEpsElements.value && pageEpsElements.value?.length <= 1);

const forceShowIfOnlyHasOnePageEps = computed(() => isPageEps.value && shouldHideWrapper.value);

const itemComponent = computed(() => {
    const componentTypes = {
        PageEps: LayersTreeItemEps,
        Group: LayersTreeItemGroup,
        CanvasElement: LayersTreeItemElement,
        default: LayersTreeItemElement
    };

    return componentTypes[props.value.type] || componentTypes.default;
});

const toggleCollapse = () => {
    onCollapsedChanged(!isCollapsed.value);
};

const onMove = (event: MoveEvent) => {
    return handleMove(event);
};

const handleDragEnd = () => {
    emit('tree-item-moved');
    store.dispatch('storeLayers/setDraggedItem', {
        currentDraggedItem: undefined,
        currentTargetItem: undefined,
    });
};

const onChangeHandler = () => {
    handleChange();
};

const emitModelUpdate = (value: ListElements[]) => {
    emit('update:modelValue', value);
};

const getLayerId = (item: ListElements) => {
    return String(item.element?.uuid || item.element?.id || item.zIndex || '');
};
</script>

<style scoped>
.layer-tree-item {
    position: relative;
    width: 100%;
    padding: 0;
}

.no-children {
    color: #999;
    font-style: italic;
    text-align: center;
    padding: 0.5rem;
}
</style>
